import { FileIcon } from '@commenda/icons/folder-icons';
import { IconButton, Icons } from '@commenda/kakashi';
import { fileIconMap } from '@commenda/utils/file-icon-map';
import React from 'react';

export interface AttachmentChipProps {
  fileName: string;
  url?: string;
  status?: 'uploading' | 'done' | 'error';
  errorMessage?: string;
  onRemove?: () => void;
  onRetry?: () => void;
}

function getExtensionLabel(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return ext.toUpperCase() || 'FILE';
}

function getFileIconComponent(fileName: string): React.ElementType {
  const extension = Object.keys(fileIconMap).find((ext) =>
    fileName.endsWith(ext)
  );
  return extension ? fileIconMap[extension] : FileIcon;
}

export function AttachmentChip({
  fileName,
  url,
  status = 'done',
  errorMessage,
  onRemove,
  onRetry,
}: AttachmentChipProps) {
  const label = getExtensionLabel(fileName);
  const isError = status === 'error';
  const isPending = status === 'uploading';
  const IconComponent = getFileIconComponent(fileName);

  const content = (
    <>
      <div className="relative shrink-0">
        <IconComponent className="size-8" />
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        <span
          className={`text-sm font-medium truncate ${isError ? 'text-red-600' : 'text-text-primary'}`}
        >
          {errorMessage ?? fileName}
        </span>
        <span
          className={`text-xs ${isError ? 'text-red-400' : 'text-text-secondary'}`}
        >
          {label}
        </span>
      </div>

      {isError && onRetry && (
        <IconButton
          type="button"
          variant="destructive"
          size="xs"
          icon={<Icons.RefreshCcw01 className="size-3" />}
          aria-label={`Retry uploading ${fileName}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRetry();
          }}
        />
      )}

      {onRemove &&
        (isPending ? (
          <span className="size-5 shrink-0 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            className="shrink-0 flex items-center justify-center size-5 rounded-full bg-gray-900 text-white hover:bg-gray-700 transition-colors"
          >
            <Icons.XClose className="size-3" />
          </button>
        ))}
    </>
  );

  const className = `flex gap-3 rounded-lg border px-3 py-2.5 max-w-[300px] min-w-0 ${
    isError ? 'border-red-200 bg-red-50' : 'border-border-primary bg-bg-primary'
  }`;

  if (url && !onRemove) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${className} hover:bg-bg-secondary transition-colors no-underline`}
      >
        {content}
      </a>
    );
  }

  return <div className={className}>{content}</div>;
}
